// lib/bidirectional-service.ts

import { convertToVue2Uniapp } from './vue2-uniapp-converter';
import { postProcessUniappCode } from './ai-converter';


export type Framework = 'html' | 'react' | 'vue' | 'uniapp';

// 转换缓存
interface ConversionCache {
  source: string;
  sourceFramework: Framework;
  target: string;
  targetFramework: Framework;
  timestamp: number;
}

const conversionCache = new Map<string, ConversionCache>();

// 生成缓存键
function getCacheKey(sourceCode: string, sourceFramework: Framework, targetFramework: Framework): string {
  const hash = simpleHash(sourceCode);
  return `${sourceFramework}_${targetFramework}_${hash}`;
}

// 简单哈希函数
function simpleHash(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash).toString(16);
}

// 清理旧缓存
function cleanupOldCache() {
  if (conversionCache.size > 100) {
    const entries = Array.from(conversionCache.entries());
    // 按时间排序，删除最早的50个
    entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
    for (let i = 0; i < 50 && i < entries.length; i++) {
      conversionCache.delete(entries[i][0]);
    }
  }
}

// 正向转换：HTML → 目标框架
export async function convertToTarget(
  htmlCode: string,
  targetFramework: Framework
): Promise<{ code: string; cached: boolean }> {
  if (!htmlCode.trim()) {
    throw new Error('HTML代码不能为空');
  }

  const cacheKey = getCacheKey(htmlCode, 'html', targetFramework);
  
  // 检查缓存
  if (conversionCache.has(cacheKey)) {
    const cached = conversionCache.get(cacheKey)!;
    console.log(`使用缓存: ${targetFramework} (${new Date(cached.timestamp).toLocaleTimeString()})`);
    return { code: cached.target, cached: true };
  }
  
  console.log(`开始转换到: ${targetFramework}`);
  
  try {
    // 调用AI转换
    const targetCode = await aiConvert(htmlCode, 'html', targetFramework);
    
    // 缓存结果
    conversionCache.set(cacheKey, {
      source: htmlCode,
      sourceFramework: 'html',
      target: targetCode,
      targetFramework,
      timestamp: Date.now()
    });
    
    cleanupOldCache();
    
    return { code: targetCode, cached: false };
  } catch (error) {
    console.error(`转换到 ${targetFramework} 失败:`, error);
    throw new Error(`转换到 ${targetFramework} 失败: ${error instanceof Error ? error.message : '未知错误'}`);
  }
}

// 反向转换：其他框架 → HTML
export async function convertBackToHtml(
  sourceCode: string,
  sourceFramework: Framework
): Promise<{ code: string; cached: boolean }> {
  if (!sourceCode.trim()) {
    throw new Error('源代码不能为空');
  }

  // 首先查找是否有对应的正向转换
  for (const [, conversion] of conversionCache) {
    if (
      conversion.target === sourceCode && 
      conversion.targetFramework === sourceFramework
    ) {
      console.log(`找到反向缓存: ${sourceFramework} → HTML`);
      return { code: conversion.source, cached: true };
    }
  }
  
  console.log(`开始反向转换: ${sourceFramework} → HTML`);
  
  try {
    const htmlCode = await aiConvert(sourceCode, sourceFramework, 'html');
    
    // 缓存反向转换结果
    const cacheKey = getCacheKey(htmlCode, 'html', sourceFramework);
    conversionCache.set(cacheKey, {
      source: htmlCode,
      sourceFramework: 'html',
      target: sourceCode,
      targetFramework: sourceFramework,
      timestamp: Date.now()
    });
    
    cleanupOldCache();
    
    return { code: htmlCode, cached: false };
  } catch (error) {
    console.error(`反向转换失败:`, error);
    throw new Error(`反向转换失败: ${error instanceof Error ? error.message : '未知错误'}`);
  }
}

// 获取所有已转换的框架代码
export function getAllConvertedCodes(htmlCode: string): Record<Framework, string | null> {
  const result: Record<Framework, string | null> = {
    html: htmlCode,
    react: null,
    vue: null,
    uniapp: null
  };
  
  // 查找所有缓存中此HTML对应的转换
  for (const [, conversion] of conversionCache) {
    if (conversion.source === htmlCode) {
      result[conversion.targetFramework] = conversion.target;
    }
  }
  
  return result;
}

// 清除所有缓存
export function clearAllCache(): void {
  conversionCache.clear();
  console.log('已清除所有转换缓存');
}

// AI转换函数（简化的模拟实现）
async function aiConvert(
  sourceCode: string,
  sourceFramework: string,
  targetFramework: string
): Promise<string> {
  // 这里应该调用实际的AI服务
  // 为了演示，我们返回模拟的转换结果
  
  console.log(`AI转换: ${sourceFramework} → ${targetFramework}`);
  
  // 模拟网络延迟
  await new Promise(resolve => setTimeout(resolve, 500));

  
// 在 aiConvert 函数中：
if (targetFramework === 'uniapp') {
  try {
    // 首先尝试本地转换器
    const vue2Code = convertToVue2Uniapp(sourceCode);
    return vue2Code;
  } catch (error) {
    console.warn('本地转换器失败，使用AI转换:', error);
    // 降级到AI转换
    const aiCode = await aiConvert(sourceCode, sourceFramework, targetFramework);
    return postProcessUniappCode(aiCode);
  }
}

function genericUniappConvert(html: string): string {
  return `<template>
  <view class="container">
    ${convertHtmlToUniappVue2(html)}
  </view>
</template>

<script>
export default {
  data() {
    return {
      message: 'Hello uni-app',
      count: 0
    }
  },
  methods: {
    handleTap() {
      this.count++
      uni.showToast({
        title: \`点击了\${this.count}次\`,
        icon: 'none'
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.container {
  padding: 20rpx;
}
</style>`;
}

  if (targetFramework === 'react') {
    return `// 转换后的 React 组件
import React from 'react';
import './styles.css';

function GeneratedComponent() {
  return (
    <div className="container">
      ${convertHtmlToJsx(sourceCode)}
    </div>
  );
}

export default GeneratedComponent;`;
  }
  
  if (targetFramework === 'vue') {
    return `<template>
  <div class="container">
    ${convertHtmlToVue(sourceCode)}
  </div>
</template>

<script setup>
// Vue 3 组合式 API
import { ref } from 'vue'

const count = ref(0)
</script>

<style scoped>
.container {
  /* Vue 样式 */
}
</style>`;
  }
  
  if (targetFramework === 'html' && sourceFramework !== 'html') {
    // 反向转换
    return `<!-- 从 ${sourceFramework.toUpperCase()} 转换回的 HTML -->
<div class="container">
  <!-- 转换内容 -->
  ${sourceCode.includes('<template>') ? 
    sourceCode.replace(/<template>[\s\S]*?<\/template>/g, '').replace(/<[^>]+>/g, '') : 
    sourceCode
  }
</div>`;
  }
  
  // 默认返回原代码（HTML到HTML）
  return sourceCode;
}

// 辅助转换函数
function convertHtmlToUniapp(html: string): string {
  return html
    .replace(/<div/g, '<view')
    .replace(/<\/div>/g, '</view>')
    .replace(/class="/g, 'class="')
    .replace(/<button/g, '<button')
    .replace(/<\/button>/g, '</button>');
}

function convertHtmlToJsx(html: string): string {
  return html
    .replace(/class="/g, 'className="')
    .replace(/for="/g, 'htmlFor="')
    .replace(/\bon([a-z]+)=/g, (match, p1) => `on${p1.charAt(0).toUpperCase() + p1.slice(1)}=`)
    .replace(/<!--[\s\S]*?-->/g, '{/* $& */}');
}

function convertHtmlToVue(html: string): string {
  return html
    .replace(/\bon([a-z]+)=/g, (match, p1) => `@${p1}=`)
    .replace(/v-bind:/g, ':')
    .replace(/v-on:/g, '@');
}


// 转换HTML为Vue 2风格的uni-app
function convertHtmlToUniappVue2(html: string): string {
  return html
    .replace(/<div/g, '<view')
    .replace(/<\/div>/g, '</view>')
    .replace(/<span/g, '<text')
    .replace(/<\/span>/g, '</text>')
    .replace(/<button/g, '<button')
    .replace(/<\/button>/g, '</button>')
    .replace(/<input/g, '<input')
    .replace(/<form/g, '<form')
    .replace(/<\/form>/g, '</form>')
    .replace(/<img/g, '<image')
    .replace(/<\/img>/g, '</image>')
    .replace(/class="/g, 'class="')
    .replace(/v-model/g, 'v-model')
    .replace(/@click/g, '@tap')  // uni-app使用@tap而不是@click
    .replace(/\bonclick=/g, '@tap=');
}

// 从HTML中提取数据
function extractDataFromHtml(html: string): string {
  const data: string[] = [];
  
  // 提取输入框的值
  if (html.includes('<input')) {
    const inputMatches = html.match(/<input[^>]*name=["']([^"']+)["']/g) || 
                        html.match(/<input[^>]*id=["']([^"']+)["']/g);
    
    if (inputMatches) {
      inputMatches.forEach(match => {
        const nameMatch = match.match(/name=["']([^"']+)["']/) || 
                         match.match(/id=["']([^"']+)["']/);
        if (nameMatch && nameMatch[1]) {
          data.push(`${nameMatch[1]}: ''`);
        }
      });
    }
  }
  
  // 提取按钮状态等
  if (html.includes('button')) {
    data.push('isLoading: false');
  }
  
  // 默认数据
  if (data.length === 0) {
    data.push('message: \'Hello uni-app\'');
    data.push('count: 0');
  }
  
  return data.join(',\n      ');
}
